import { CSEModal, CSEStepperInput } from '@cloudsuite/mosaic-elements'
import { CSSResultGroup, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ProductAddedEvent } from 'components/ordering/multiple-baskets/basket-events.js'
import { AddToBasketModalError, AddToBasketSuccess } from 'components/ordering/add-to-basket/basket-parser.js'
import {
  BASKET_MUTATOR,
  BASKET_STORE,
  BasketMutator,
  BasketStore
} from 'components/ordering/order-form/basket-store.js'
import { PdpActionsContext, PdpQuantityContext, type PdpActionsContextType, type PdpQuantityContextType } from 'components/discovery/pdp/provider.js'
import {
  ProductOptionsChangedEvent,
  ProductQuantityChangedEvent,
  ProductSelectionRequestedEvent,
} from 'components/discovery/pdp/shared/pdp-selection-events.js'
import type { SelectedBasketOption } from 'components/ordering/order-form/add-to-basket-api.js'
import { DEFAULT_ROLE_PERMISSIONS, RolePermissionsContext, type RolePermissions } from 'components/shared/role/role-provider.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { style } from './order-form.style.js'
import type { OrderFormButtonState, OrderFormLayout } from './types.js'

export const fitVariants = ['auto', 'fill', 'square'] as const
export type FitVariant = typeof fitVariants[number]

export const orderFormHiddenFields = ['stepper', 'uom'] as const

@customElement('csc-order-form')
export class CSCOrderForm extends HiddenFieldsMixin(LitElement, orderFormHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(BASKET_STORE)
  private basketStore!: BasketStore

  @inject(BASKET_MUTATOR)
  private basketMutator!: BasketMutator

  @consume({ context: PdpActionsContext })
  private pdpActions?: PdpActionsContextType

  @consume({ context: PdpQuantityContext, subscribe: true })
  @state()
  private contextQuantity: PdpQuantityContextType = 0

  private selectedOptions: SelectedBasketOption[] = []

  @consume({ context: RolePermissionsContext, subscribe: true })
  @state()
  private rolePermissions: RolePermissions = DEFAULT_ROLE_PERMISSIONS

  @property({ type: Number, attribute: 'min' })
  minimum: number = 0

  @property({ type: Number, attribute: 'max' })
  maximum?: number

  @property({ type: Number, attribute: 'step' })
  step: number = 1

  @property({ type: Number, attribute: 'default' })
  defaultValue: number = 1

  @property({ type: Number, attribute: 'product-id' })
  productId!: number

  @property({ type: String, attribute: 'layout' })
  layout: OrderFormLayout = 'default'

  @property({ type: Boolean, attribute: 'cannot-be-added-to-basket' })
  cannotBeAddedToBasket: boolean = false

  @property({ type: Boolean, attribute: 'show-button-label' })
  showButtonLabel: boolean = false

  @state()
  private legacyHideStepper = false

  /** @deprecated Use `hidden-fields="stepper"` instead. */
  @property({ type: Boolean, attribute: 'hide-stepper' })
  set hideStepper(value: boolean) {
    console.warn('<csc-order-form> "hide-stepper" is deprecated, use hidden-fields="stepper" instead.')
    this.legacyHideStepper = value
  }

  get hideStepper() {
    return this.legacyHideStepper || this.isHidden('stepper')
  }

  // fill the parent, or shrink to the form's own content with auto/square so a centered parent
  // (e.g. a centered tile zone) can center it.
  @property({ type: String, reflect: true })
  fit: FitVariant = 'fill'

  @state()
  quantity: number | undefined

  @state()
  private quantityInBasket: number | undefined

  private quantitySettled = false

  @state()
  private orderFormErrorState: 'inline' | 'modal' | 'all-good' = 'all-good'

  @state()
  private inlineErrorMessage?: string

  @state()
  private modalErrorMessage?: string

  @state()
  private buttonState: OrderFormButtonState = 'initial'


  @query('cse-stepper-input')
  private stepperInput!: CSEStepperInput

  @query('cse-modal')
  private errorModal!: CSEModal

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('contextQuantity') && this.contextQuantity > 0 && this.contextQuantity !== this.quantity) {
      this.quantity = this.contextQuantity
    }
    if (changedProperties.has('quantity')) {
      this.updateButtonState()
      this.publishQuantity()
    }
  }

  override connectedCallback() {
    super.connectedCallback()

    window.addEventListener(ProductOptionsChangedEvent.type, this.receiveSelectedOptions)
    window.addEventListener(ProductSelectionRequestedEvent.type, this.answerSelectionRequest)
    if (this.productId) this.dispatchEvent(new ProductSelectionRequestedEvent(this.productId))

    this.quantitySettled = this.quantity !== undefined

    createEffect(() => {
      this.quantityInBasket = this.basketStore.productInBasket(this.productId)?.quantity
      this.quantity = this.resolveQuantity()
      this.updateButtonState()
    })
  }

  override disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener(ProductOptionsChangedEvent.type, this.receiveSelectedOptions)
    window.removeEventListener(ProductSelectionRequestedEvent.type, this.answerSelectionRequest)
  }

  private receiveSelectedOptions = (event: ProductOptionsChangedEvent) => {
    if (event.productId === this.productId) this.selectedOptions = event.options
  }

  private answerSelectionRequest = (event: ProductSelectionRequestedEvent) => {
    if (event.productId === this.productId && event.composedPath()[0] !== this) this.publishQuantity()
  }

  private publishQuantity = () => {
    if (this.quantity !== undefined) this.dispatchEvent(new ProductQuantityChangedEvent(this.productId, this.quantity))
  }

  private resolveQuantity = (): number => {
    if (this.quantityInBasket !== undefined && !this.quantitySettled) {
      this.quantitySettled = true
      return this.quantityInBasket
    }

    if (this.quantity !== undefined) return this.quantity
    if (this.quantityInBasket !== undefined) return this.quantityInBasket
    return this.defaultValue
  }

  private updateButtonState = () => {
    if (!this.quantityInBasket) {
      this.buttonState = 'initial'
      return
    }

    this.buttonState = this.quantity !== this.quantityInBasket ? 'reorder' : 'confirmed'
  }

  private addToBasket = async () => {
    if (this.quantity === undefined) { return }
    const quantity = this.quantity
    const previousQuantity = this.quantityInBasket ?? 0

    this.buttonState = 'pending'

    const result = await this.basketMutator.addToBasket(this.productId, quantity, this.selectedOptions)
    if (result instanceof AddToBasketSuccess) {
      const addedQuantity = quantity - previousQuantity
      this.dispatchEvent(new ProductAddedEvent(result.basketCount, result.itemId, addedQuantity > 0 ? addedQuantity : undefined))
      delete this.inlineErrorMessage
      this.updateButtonState()
    } else if (result instanceof AddToBasketModalError) {
      this.buttonState = 'error'
      this.orderFormErrorState = 'modal'
      this.modalErrorMessage = result.message
      this.errorModal.open()
    } else {
      this.buttonState = 'error'
      this.orderFormErrorState = 'inline'
      this.inlineErrorMessage = result.message
    }
  }

  private updateQuantity = () => {
    this.quantity = parseFloat(this.stepperInput.value) || this.minimum
    this.updateButtonState()
    this.pdpActions?.setQuantity(this.quantity)
  }

  private renderButton = () => {
    return html`
      <csc-order-form-button state="${this.buttonState}" layout="${this.layout}" ?show-label="${this.showButtonLabel}" @order-form-add="${this.addToBasket}"></csc-order-form-button>
    `
  }

  override render() {
    if (!this.rolePermissions.createOrders) return nothing

    if (this.cannotBeAddedToBasket) return nothing

    return html`
      <div class="order-form layout-${this.layout}">
        ${!this.hideStepper ? html`
          <cse-form-field>
            <cse-stepper-input
              min="${this.minimum}"
              max="${ifDefined(this.maximum)}"
              step="${this.step}"
              value="${ifDefined(this.quantity)}"
              @change="${this.updateQuantity}"
              valid="${this.orderFormErrorState === 'inline' ? 'error' : 'none'}"
            >
            </cse-stepper-input>
            ${this.isHidden('uom') ? nothing : html`<slot name="uom"></slot>`}
            ${this.inlineErrorMessage && this.inlineErrorMessage.length > 0
          ? html`
                <span slot="invalid-text">
                  ${this.inlineErrorMessage}
                </span>
              ` : nothing
        }
            </span>
          </cse-form-field>
        ` : this.isHidden('uom') ? nothing : html`<slot name="uom"></slot>`}

        ${this.renderButton()}
      </div>

      <cse-modal variant="error">
        <div slot="header">
          ${t('modal_header_text', { 'ns': 'csc-order-form', 'default': 'Unable to add product' })}
        </div>
        <cse-icon
          icon-role="csc-order-form.error-modal"
          size="xl"
          slot="icon"
        ></cse-icon>
        <cse-text>
          ${this.modalErrorMessage}
        </cse-text>
        <cse-button @click="${this.closeErrorModal}" slot="footer" fit="fill">
          ${t('modal_close_button_text', { 'ns': 'csc-order-form', 'default': 'Close' })}
        </cse-button>
      </cse-modal>
    `
  }

  private closeErrorModal = () => {
    this.errorModal.close()
  }
}

initializeIconsByRole({
  'csc-order-form.initial': { name: 'basket-shopping', style: 'regular' },
  'csc-order-form.pending': { name: 'spinner', style: 'regular' },
  'csc-order-form.confirmed': { name: 'check', style: 'regular' },
  'csc-order-form.reorder': { name: 'arrows-rotate', style: 'regular' },
  'csc-order-form.error': { name: 'xmark', style: 'regular' },
  'csc-order-form.error-modal': { name: 'triangle-exclamation', style: 'regular' },
})

declare global {
  interface HTMLElementTagNameMap {
    'csc-order-form': CSCOrderForm
  }
}
